import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { propTypes } from '../../util/types';
import {
  buildPerformerSubpageSchema,
  extractFaqsFromBlocks,
} from '../../util/seoDiscoverySchema';
import SeoBreadcrumbs from '../../components/SeoDiscovery/SeoBreadcrumbs';
import SeoContentSections from '../../components/SeoDiscovery/SeoContentSections';
import SeoEventCalendar, {
  ALL_UPCOMING_EVENTS_ANCHOR_ID,
} from '../../components/SeoDiscovery/SeoEventCalendar';
import SeoUpcomingEvents from '../../components/SeoDiscovery/SeoUpcomingEvents';
import SeoFaqSection from '../../components/SeoDiscovery/SeoFaqSection';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';

const rootUrl = () =>
  typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');

export const SeoDiscoveryPerformerSubpageComponent = props => {
  const { payload, fetchInProgress, fetchError, staticContext } = props;

  if (fetchError?.status === 404 && staticContext) {
    staticContext.notfound = true;
  }

  if (fetchInProgress && !payload) {
    return null;
  }

  const performer = payload?.performer;
  if (!performer && fetchError?.status === 404) {
    return (
      <Page title="Performer page not found | PromoTix">
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <h1 className={css.h1}>Performer page not found</h1>
            <p>
              <a href="/event-discovery/performers/">Browse all performers</a>
            </p>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  if (!performer) {
    return null;
  }

  const {
    venuesPlayed = [],
    venues = [],
    upcomingEvents = [],
    contentBlocks = [],
    internalLinks = [],
    breadcrumbs = [],
    meta = {},
    page,
    pageKey,
    subpageSlug,
  } = payload;
  const venueList = venuesPlayed.length ? venuesPlayed : venues;
  const noIndex = page?.indexState && page.indexState !== 'index';
  const isEvents = pageKey === 'upcoming-events' || subpageSlug === 'events';
  const pageTitle =
    meta.title || meta.metaTitle || `${meta.h1 || performer.performerName} | PromoTix`;
  const pageDescription =
    meta.description ||
    meta.metaDescription ||
    `Find tickets and venues for ${performer.performerName} on PromoTix.`;
  const faqs = extractFaqsFromBlocks(contentBlocks);
  const performerHubPath = `/event-discovery/performers/${performer.performerSlug || performer.slug || ''}/`;
  const canonicalPath =
    page?.canonicalPath ||
    (isEvents ? `${performerHubPath}events/` : `${performerHubPath}venues/`);
  const schema = buildPerformerSubpageSchema({
    performer,
    performerHubPath,
    canonicalPath,
    rootUrl: rootUrl(),
    breadcrumbs,
    faqs,
    venues: venueList,
    upcomingEvents,
    meta: {
      ...meta,
      metaTitle: pageTitle,
      metaDescription: pageDescription,
    },
    pageKey: isEvents ? 'upcoming-events' : 'venues',
  });

  return (
    <Page title={pageTitle} description={pageDescription} schema={schema}>
      {noIndex ? (
        <Helmet>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
      ) : null}
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {breadcrumbs.length ? <SeoBreadcrumbs items={breadcrumbs} /> : null}
          <h1 className={css.h1}>{meta.h1}</h1>
          {isEvents ? (
            <>
              <SeoEventCalendar
                events={upcomingEvents}
                venueName={performer.performerName}
                showImages={false}
                moreEventsHref={`#${ALL_UPCOMING_EVENTS_ANCHOR_ID}`}
              />
              <SeoContentSections blocks={contentBlocks} excludeHero />
              <SeoUpcomingEvents
                id={ALL_UPCOMING_EVENTS_ANCHOR_ID}
                events={upcomingEvents}
                showImages={false}
                title={`All upcoming ${performer.performerName} events`}
                emptyMessage={
                  payload?.inventorySystemError
                    ? `Live listings for ${performer.performerName} are temporarily unavailable.`
                    : `There are no live listings for ${performer.performerName} right now.`
                }
              />
            </>
          ) : (
            <>
              <SeoContentSections blocks={contentBlocks} excludeHero />
              <section className={css.section}>
                <h2>Venues for {performer.performerName}</h2>
                {venueList.length ? (
                  <ul className={css.indexList}>
                    {venueList.map(v => (
                      <li key={v.venueId || v.slug || v.venueName}>
                        {v.path ? <a href={v.path}>{v.venueName}</a> : v.venueName}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No venues listed yet.</p>
                )}
              </section>
            </>
          )}
          {internalLinks.filter(l => l.placement === 'breadcrumb' || l.placement === 'hub_nav')
            .length ? (
            <nav className={css.section} aria-label="Performer navigation">
              <ul className={css.indexList}>
                {internalLinks
                  .filter(l => l.placement === 'breadcrumb' || l.placement === 'hub_nav')
                  .map(l => (
                    <li key={l.linkId || l.targetPath}>
                      <a href={l.targetPath}>{l.anchorText}</a>
                    </li>
                  ))}
              </ul>
            </nav>
          ) : null}
          <SeoFaqSection blocks={contentBlocks} />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoDiscoveryPerformerSubpageComponent.propTypes = {
  payload: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
  staticContext: object,
};

const mapStateToProps = state => state.SeoDiscoveryPerformerSubpage || {};

export default compose(withRouter, connect(mapStateToProps))(SeoDiscoveryPerformerSubpageComponent);
